import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type React from "react";

type AnchorRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

function rectFromElement(element: HTMLElement | null): AnchorRect | null {
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height
  };
}

export function WebsitePopover({
  anchorRef,
  children,
  className = "",
  onClose,
  open,
  placement = "bottom-end",
  width = 320
}: {
  anchorRef: React.RefObject<HTMLElement>;
  children: React.ReactNode;
  className?: string;
  onClose: () => void;
  open: boolean;
  placement?: "bottom-end" | "bottom-start";
  width?: number;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [anchorRect, setAnchorRect] = useState<AnchorRect | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    setAnchorRect(rectFromElement(anchorRef.current));
  }, [anchorRef, open]);

  useEffect(() => {
    if (!open) return;

    function closeOnKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        anchorRef.current?.focus();
      }
    }

    function closeOnPointer(event: PointerEvent) {
      const target = event.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose();
    }

    function syncPosition() {
      setAnchorRect(rectFromElement(anchorRef.current));
    }

    window.addEventListener("keydown", closeOnKey);
    window.addEventListener("pointerdown", closeOnPointer, true);
    window.addEventListener("resize", syncPosition);
    window.addEventListener("scroll", syncPosition, true);

    return () => {
      window.removeEventListener("keydown", closeOnKey);
      window.removeEventListener("pointerdown", closeOnPointer, true);
      window.removeEventListener("resize", syncPosition);
      window.removeEventListener("scroll", syncPosition, true);
    };
  }, [anchorRef, onClose, open]);

  if (!open || !anchorRect) return null;

  const margin = 10;
  const top = Math.min(window.innerHeight - 80, anchorRect.top + anchorRect.height + margin);
  const rawLeft = placement === "bottom-start" ? anchorRect.left : anchorRect.left + anchorRect.width - width;
  const left = Math.max(12, Math.min(rawLeft, window.innerWidth - width - 12));

  return createPortal(
    <div
      className={`fd-web-popover ${className}`}
      ref={panelRef}
      role="dialog"
      style={{ top, left, width }}
    >
      {children}
    </div>,
    document.body
  );
}

export function WebsiteModal({
  children,
  onClose,
  open = true,
  title,
  width = 760
}: {
  children: React.ReactNode;
  onClose: () => void;
  open?: boolean;
  title: string;
  width?: number;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function closeOnKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    }

    window.addEventListener("keydown", closeOnKey);
    window.setTimeout(() => dialogRef.current?.focus(), 0);
    return () => window.removeEventListener("keydown", closeOnKey);
  }, [onClose, open]);

  if (!open) return null;

  return createPortal(
    <div className="fd-web-modal-backdrop" onMouseDown={onClose}>
      <div
        aria-modal="true"
        className="fd-web-modal-card"
        onMouseDown={(event) => event.stopPropagation()}
        ref={dialogRef}
        role="dialog"
        style={{ width: `min(${width}px, calc(100vw - 32px))` }}
        tabIndex={-1}
      >
        <div className="fd-web-modal-titlebar">
          <h2>{title}</h2>
          <button aria-label="Sluiten" className="fd-web-modal-close" onClick={onClose} type="button">×</button>
        </div>
        <div className="fd-web-modal-body">{children}</div>
      </div>
    </div>,
    document.body
  );
}
